import { COUNT_ITEM } from '@doltech/ui/lib/figma/DolEditor/elements/sentences-count/renderElement';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { Element } from 'slate';
import styled from 'styled-components';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { RendererWithPlugins } from '@doltech/ui/lib/slatePlugins/components/core/readonly/RendererWithPlugins';
import { ParagraphPlugin } from '@doltech/ui/lib/slatePlugins/components/core/readonly/ParagraphPlugin';
import { BoldReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/bold/BoldReadOnlyPlugin';
import { ItalicReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/italic/ItalicReadOnlyPlugin';
import { StrikethroughReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/strikethrough/StrikethroughReadOnlyPlugin';
import {
  TextColorPlugin,
  TextColorReadOnlyPlugin,
} from '@doltech/ui/lib/figma/DolEditor/marks/text-color/TextColorPlugin';
import { HighlightElementReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/highlight/HighlightReadOnlyPlugin';
import { DEFAULT_HIGHLIGHT_COLORS_LIST } from '@doltech/ui/lib/figma/DolEditor/elements/highlight/defaults';
import { TextTransformPlugin } from '@doltech/ui/lib/figma/BlogEditor/plugins/TextTransform/TextTransformPlugin';
import { HeadingReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/elements/heading/HeadingReadOnlyPlugin';
import { BulletPointReadOnlyPlugin } from '@doltech/ui/lib/figma/BlogEditor/plugins/BulletPoint/BulletPointReadOnlyPlugin';
import { IconReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/icons/IconPlugin';
import { QuestionStyleReadOnlyPlugin } from '@doltech/ui/lib/figma/BlogEditor/plugins/QuestionStyle/QuestionStyleReadOnlyPlugin';
import { RoundedElementReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/rounded/plugin';
import { AbbrReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/abbr/AbbrReadOnlyPlugin';
import { CiteReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/cite/CiteReadOnlyPlugin';
import { QReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/q/QReadOnlyPlugin';
import { QuoteReadOnlyPlugin } from '@doltech/ui/lib/figma/BlogEditor/plugins/Quote/QuoteReadOnlyPlugin';
import { UploadImageReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/UploadImage/UploadImageReadOnlyPlugin';
import { UnderlineReadOnlyPlugin } from '@doltech/ui/lib/slatePlugins/marks/underline';
import { UnderlineColorReadOnlyPlugin } from '@doltech/ui/lib/figma/DolEditor/marks/underline-color/UnderlineColorPlugin';
import { NumberingElementPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/numbering/plugin';
import { NoteElementPlugin } from '@doltech/ui/lib/figma/DolEditor/elements/note/plugin';

const ExplanationCard = dynamic(import('./ExplanationCard'));

export const EditorWrapper = styled.div`
  font-family: Inter, sans-serif;
  color: ${colorsV2.grey180};
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;

  p {
    word-break: break-word;
    white-space: pre-wrap;
  }

  h3 {
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }
`;

interface ParaphrasingComponentProp extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  icon?: React.ReactNode;
  initValue?: any;
  showTitle?: boolean;
  limit?: number | boolean;
  withCardLayout?: boolean;
}

export const ParaphrasingViewOnlyComponent = (props: ParaphrasingComponentProp) => {
  const { title, initValue, showTitle, limit = false, withCardLayout = true, icon } = props;

  const getData = (data: any) => {
    if (!data) {
      return undefined;
    }
    if (Array.isArray(data)) {
      return data;
    }

    const result = Array.from(JSON.parse(data)).filter((n) => Element.isElement(n)) as any;
    ((result[0] as any)?.children || []).forEach((item, index) => {
      if (item.type === COUNT_ITEM) {
        result[0].children.splice(index, 1);
      }
    });

    if (limit) {
      result.splice(limit);
    }
    return result;
  };

  const plugins = [
    // heading
    HeadingReadOnlyPlugin(),
    ParagraphPlugin,
    BulletPointReadOnlyPlugin(),
    IconReadOnlyPlugin(),
    QuestionStyleReadOnlyPlugin(), // a rounded circle text to indicate question number

    // text format
    BoldReadOnlyPlugin(),
    ItalicReadOnlyPlugin(),
    UnderlineColorReadOnlyPlugin(),
    UnderlineReadOnlyPlugin(),
    StrikethroughReadOnlyPlugin(),
    TextColorReadOnlyPlugin(),
    TextColorPlugin(),
    HighlightElementReadOnlyPlugin({
      colors: DEFAULT_HIGHLIGHT_COLORS_LIST,
    }),
    NoteElementPlugin(),
    TextTransformPlugin(), // uppercase, titlecase, lowercase
    QuoteReadOnlyPlugin(),
    // math
    NumberingElementPlugin(),
    RoundedElementReadOnlyPlugin(), // rounded text with annotation
    AbbrReadOnlyPlugin(),
    CiteReadOnlyPlugin(),
    QReadOnlyPlugin(),
    UploadImageReadOnlyPlugin({ isPreview: false }),
  ];

  if (withCardLayout) {
    return (
      <ExplanationCard icon={icon} title={title} showTitle={showTitle}>
        <EditorWrapper>
          <RendererWithPlugins value={getData(initValue) as any} plugins={plugins} />
        </EditorWrapper>
      </ExplanationCard>
    );
  }

  return (
    <EditorWrapper>
      <RendererWithPlugins value={getData(initValue) as any} plugins={plugins} />
    </EditorWrapper>
  );
};
